import React, {
 useCallback,
 useEffect,
 useMemo,
 useState,
} from 'react'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import KTTable from 'helpers/ui-components/KTTable'
import {ColumnProps} from 'models/Common'
import {IProductCategoryLicense} from 'models/MyAccount'
import Spinner from 'layout/common/Spinner'
import {KTConfirmPopup} from 'helpers/ui-components/KTConfirmPopup'
import {
 addProductCategoryLicense,
 deleteProductCategoryLicense,
 fetchProductCategoryLicense,
 updateProductCategoryLicense,
} from 'utils/api/auth/myAccountService'
import {fetchState} from 'utils/api/auth/locationService'
import {
 formatDateForDB,
 formatDateForDisplay,
 generateRandomString,
} from 'utils/helper'
import {toast} from 'react-toastify'
import IDropdown from 'models/dropdown'
import AddMandiLicenseModal, {
 IMandiLicenseFormPayload,
} from './AddMandiLicenseModal'

type IProps = {
 userId?: string
}

const MandiLicenseSection = (props: IProps) => {
 const {t} = useTranslation()
 const [loader, setLoader] = useState(false)
 const [licenseList, setLicenseList] = useState<
  IProductCategoryLicense[]
 >([])
 const [showLicenseModal, setShowLicenseModal] =
  useState(false)
 const [editLicense, setEditLicense] =
  useState<IProductCategoryLicense | null>(null)
 const [deleteLicenseItem, setDeleteLicenseItem] =
  useState<IProductCategoryLicense | null>(null)

 const loadLicenseList = useCallback(async () => {
  setLoader(true)
  const [list, states] = await Promise.all([
   fetchProductCategoryLicense(props.userId, 'output'),
   fetchState(),
  ])
  const stateOptions: IDropdown[] = Array.isArray(states)
   ? states
   : []
  const stateNameById = new Map(
   stateOptions.map((item) => [Number(item.id), item.name])
  )
  const normalized: IProductCategoryLicense[] =
   Array.isArray(list)
    ? list.map((item: IProductCategoryLicense) => ({
       ...item,
       key: item.key || generateRandomString(5),
       state_name:
        item.state_name ||
        stateNameById.get(Number(item.state)) ||
        String(item.state ?? ''),
      }))
    : []
  setLicenseList(normalized)
  setLoader(false)
 }, [props.userId])

 useEffect(() => {
  void loadLicenseList()
 }, [loadLicenseList])

 const handleOpenAdd = () => {
  setEditLicense(null)
  setShowLicenseModal(true)
 }

 const handleOpenEdit = (
  license: IProductCategoryLicense
 ) => {
  setEditLicense(license)
  setShowLicenseModal(true)
 }

 const handleCloseModal = () => {
  setShowLicenseModal(false)
  setEditLicense(null)
 }

 const handleSaveLicense = async (
  values: IMandiLicenseFormPayload
 ) => {
  setLoader(true)
  const expiryDate =
   values.expiry_date instanceof Date
    ? formatDateForDB(values.expiry_date)
    : values.expiry_date

  const payload = {
   state: values.state,
   license_number: values.license_number,
   expiry_date: expiryDate,
   license_document: values.license_document,
   business_service: 'output',
   ...(values.license_document_url && {
    license_document_url: values.license_document_url,
   }),
   ...(values.id && {id: values.id}),
  } as IProductCategoryLicense

  const res = values.id
   ? await updateProductCategoryLicense(
      [payload],
      props.userId
     )
   : await addProductCategoryLicense(
      [payload],
      props.userId
     )

  setLoader(false)

  if (res) {
   toast.success(
    res.message || 'License saved successfully'
   )
   handleCloseModal()
   await loadLicenseList()
  }
 }

 const handleDeleteConfirm = async (isConfirm: boolean) => {
  if (isConfirm && deleteLicenseItem?.id) {
   setLoader(true)
   const res = await deleteProductCategoryLicense({
    license_id: deleteLicenseItem.id,
    ...(props.userId && {user_id: Number(props.userId)}),
   })
   setLoader(false)
   if (res) {
    await loadLicenseList()
   }
  }
  setDeleteLicenseItem(null)
 }

 const columns: Array<
  ColumnProps<IProductCategoryLicense>
 > = useMemo(
  () => [
   {
    key: 'geographyLevel',
    title: t(
     'myAccountPage.businessProfile.offtaker.mandiLicense.geographyLevel'
    ),
    render: (_col, item) => (
     <span className='truncate max-w-[160px] block'>
      {item.state_name || '-'}
     </span>
    ),
   },
   {
    key: 'licenseNumber',
    title: t(
     'myAccountPage.businessProfile.offtaker.mandiLicense.licenseNumber'
    ),
    render: (_col, item) => item.license_number || '-',
   },
   {
    key: 'expiryDate',
    title: t(
     'myAccountPage.businessProfile.offtaker.mandiLicense.expiryDate'
    ),
    render: (_col, item) =>
     item.expiry_date
      ? formatDateForDisplay(
         typeof item.expiry_date === 'string'
          ? item.expiry_date
          : formatDateForDB(item.expiry_date)
        )
      : '-',
   },
   {
    key: 'document',
    title: t(
     'myAccountPage.businessProfile.offtaker.mandiLicense.document'
    ),
    render: (_col, item) => {
     const documentUrl =
      item.license_document_url || item.license_document
     return documentUrl ? (
      <a
       href={documentUrl}
       target='_blank'
       rel='noreferrer'
       className='text-[#4E83E4] underline text-sm'
       onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(
         documentUrl,
         '_blank',
         'noopener,noreferrer'
        )
       }}
      >
       {t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.uploadedDocument'
       )}
      </a>
     ) : (
      '-'
     )
    },
   },
   {
    key: 'action',
    title: t(
     'myAccountPage.businessProfile.offtaker.mandiLicense.action'
    ),
    render: (_col, item) => (
     <div className='flex items-center gap-3'>
      <KTSVG
       path='/media/icons/pencil.svg'
       svgClassName='w-4 h-4 cursor-pointer'
       onClick={() => handleOpenEdit(item)}
      />
      <span
       className='fa fa-trash text-lg cursor-pointer text-gray-700'
       onClick={() => setDeleteLicenseItem(item)}
      />
     </div>
    ),
   },
  ],
  [t]
 )

 return (
  <>
   {loader && <Spinner />}
   {deleteLicenseItem && (
    <KTConfirmPopup
     showConfirmModel={!!deleteLicenseItem}
     handleConfirmBtnClick={handleDeleteConfirm}
     yesBtnText={t('common.yes')}
     noBtnText={t('common.no')}
     title={t('common.confirmModalTitle')}
     subTitle={t(
      'myAccountPage.businessProfile.offtaker.mandiLicense.deleteConfirm'
     )}
    />
   )}
   <div className='w-full'>
    <div className='text-[#33393D] text-lg font-bold mb-4'>
     {t(
      'myAccountPage.businessProfile.offtaker.mandiLicense.title'
     )}
    </div>
    <KTTable
     data={licenseList}
     columns={columns}
     headerClass='bg-[#80868C]'
    />
    <div className='-mt-2 border-[#E6EBF0] border-[0.5px] py-3 px-4 flex items-center w-full'>
     <div
      className='flex text-[#4E83E4] text-lg font-bold items-center cursor-pointer gap-1'
      onClick={handleOpenAdd}
     >
      <KTSVG
       path='/media/icons/plus.svg'
       svgClassName='w-4 h-4'
      />
      <div>
       {t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.addLicense'
       )}
      </div>
     </div>
    </div>
    <AddMandiLicenseModal
     show={showLicenseModal}
     editLicense={editLicense}
     onClose={handleCloseModal}
     onSave={(values) => {
      void handleSaveLicense(values)
     }}
    />
   </div>
  </>
 )
}

export default MandiLicenseSection
